'use client';

import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getUserTaskDocuments } from 'src/api/admin/documents';

import { getTask } from 'src/api/admin/tasks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/tasks/$taskId')({
	loader: async ({ params }) => {
		const { userId, taskId } = params;
		const task = await getTask(taskId);

		if (!task) {
			return { task: undefined, documents: [] };
		}

		const documents = await getUserTaskDocuments(userId, task.documentIds || []);
		return { task, documents };
	},
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { userId, taskId } = Route.useParams();
	const router = useRouter();
	const queryClient = useQueryClient();
	const { task: initialTask, documents: initialDocuments } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: task, refetch: refetchTask } = useQuery({
		queryKey: ['task', taskId],
		queryFn: () => getTask(taskId),
		initialData: initialTask,
	});

	const { data: documents } = useQuery({
		queryKey: ['task-documents', userId, task?.documentIds],
		queryFn: () => getUserTaskDocuments(userId, task?.documentIds || []),
		initialData: initialDocuments,
		enabled: !!task,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchTask();
		queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-documents', userId] });
	};

	const handleDelete = () => {
		// Navigate away first, then invalidate caches (without refetching the deleted task)
		router.navigate({ to: `/users/${userId}/tasks` });
		queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-documents', userId] });

		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
	};

	const handleClose = () => {
		// Navigate back to tasks list
		router.history.back();
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/users/${userId}/documents/${documentId}`, '_blank');
	};

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this task. Maybe it has been deleted?"
				action={{
					label: 'Return to tasks',
					href: `/users/${userId}/tasks`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<TaskViewer
			userId={userId}
			task={task}
			documents={documents || []}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onDelete={handleDelete}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: verifyPermission(PERMISSIONS.adminUserTasksDelete),
				taskDo: verifyPermission(PERMISSIONS.adminUserTasksDo),
			}}
		/>
	);
}
